// ---------------------------------------------------------------------------
// Feature Grid Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface Feature {
  icon?: string;
  heading: string;
  description?: string;
}

interface FeatureGridProps {
  heading?: string;
  subheading?: string;
  features: Feature[];
  columns?: number;
  variant?: "card" | "minimal" | "icon-left";
}

function renderFeatureCard(feature: Feature, variant: string): string {
  const iconHtml = feature.icon
    ? `<span class="ease-feature-icon">${text(feature.icon)}</span>`
    : "";

  const headingHtml = `<h3 class="ease-feature-heading">${text(feature.heading)}</h3>`;

  const descHtml = feature.description
    ? `<p class="ease-feature-description">${text(feature.description)}</p>`
    : "";

  if (variant === "icon-left") {
    return `    <div class="ease-feature-item ease-feature-item--icon-left" style="display: flex; gap: 1rem; align-items: flex-start;">
      ${iconHtml}
      <div class="ease-feature-body">
        ${headingHtml}
        ${descHtml}
      </div>
    </div>`;
  }

  const cardStyle = variant === "card"
    ? ' style="border: 1px solid var(--ease-card-border,var(--ease-color-border,var(--ease-color-muted,#e5e7eb))); border-radius: var(--ease-card-radius,var(--ease-radius,8px)); padding: var(--ease-card-padding,1.5rem); box-shadow: var(--ease-card-shadow,none); background: var(--ease-color-surface,var(--ease-color-bg,#fff));"'
    : "";

  return `    <div class="ease-feature-item ease-feature-item--${attr(variant)}"${cardStyle}>
      ${iconHtml}
      ${headingHtml}
      ${descHtml}
    </div>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as FeatureGridProps;

  const features = Array.isArray(p.features) ? p.features : [];
  const columns = Math.max(2, Math.min(4, p.columns ?? 3));
  const variant = p.variant ?? "card";

  const headingHtml = p.heading
    ? `<h2 class="ease-feature-grid-heading">${text(p.heading)}</h2>`
    : "";

  const subheadingHtml = p.subheading
    ? `\n  <p class="ease-feature-grid-subheading">${text(p.subheading)}</p>`
    : "";

  const headerHtml = headingHtml
    ? `\n  ${headingHtml}${subheadingHtml}`
    : "";

  const itemsHtml = features
    .map((f) => renderFeatureCard(f, variant))
    .join("\n");

  const ariaLabel = p.heading ?? "Features";

  return `<section class="ease-section ease-feature-grid" aria-label="${attr(ariaLabel)}">${headerHtml}
  <div class="ease-features" style="display: grid; grid-template-columns: repeat(${attr(String(columns))}, 1fr); gap: var(--ease-spacing-lg, 2rem);">
${itemsHtml}
  </div>
</section>`;
}

registerRenderer("feature-grid", { render });
